import React, { useEffect, useState } from "react";
import { api, PUBLIC_HOST } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  TrophyIcon,
  ArrowUpRightIcon,
  XIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * One-time celebratory banner shown to users after their 3rd win.
 * Nudges them to share their Bidorah collector profile with friends —
 * every share is free inbound traffic for the platform.
 */
export default function CollectorNudgeBanner() {
  const { user } = useAuth();
  const [nudge, setNudge] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!user) return;
    api
      .get("/me/collector-nudge")
      .then((r) => setNudge(r.data))
      .catch(() => setNudge({ should_show: false }));
  }, [user]);

  if (!user || hidden || !nudge?.should_show) return null;

  const dismiss = async (e) => {
    e?.stopPropagation?.();
    setHidden(true);
    try {
      await api.post("/me/collector-nudge/dismiss");
    } catch {}
  };

  const share = async () => {
    const url = `${PUBLIC_HOST}${nudge.profile_url}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Bidorah collection",
          text: `Check out what I've won on Bidorah — ${nudge.win_count} auction wins and counting.`,
          url,
        });
        await dismiss();
        return;
      } catch {}
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied. Paste it anywhere.");
      await dismiss();
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-[#1D78AB]/5 p-5 sm:p-6 mb-6"
      data-testid="collector-nudge-banner"
    >
      {/* Decorative sparkle glow */}
      <div
        aria-hidden
        className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-amber-300/30 blur-3xl pointer-events-none"
      />

      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
        aria-label="Dismiss"
        data-testid="collector-nudge-dismiss"
      >
        <XIcon size={16} />
      </button>

      <div className="flex items-start gap-4 relative z-10">
        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shrink-0 shadow-lg shadow-amber-500/30">
          <TrophyIcon size={24} weight="fill" className="text-amber-50" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase bg-amber-100 text-amber-800 rounded-full px-2 py-1 mb-2">
            <SparkleIcon size={10} weight="fill" /> Milestone unlocked
          </div>
          <h3 className="font-display font-black text-xl sm:text-2xl tracking-tighter text-zinc-900 leading-tight">
            You've got {nudge.win_count} wins —
            <br className="hidden sm:block" />
            time to flex.
          </h3>
          <p className="mt-1.5 text-sm text-zinc-600">
            Your public Bidorah collector profile is live. Share it and let
            every buddy who doubted your collection see the scoreboard.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              onClick={share}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-sm font-bold"
              data-testid="collector-nudge-share"
            >
              Share my collection <ArrowUpRightIcon size={14} weight="bold" />
            </button>
            <Link
              to={nudge.profile_url}
              className="inline-flex items-center px-4 py-2 rounded-full border border-zinc-300 hover:border-zinc-400 bg-white text-zinc-800 text-sm font-semibold"
              data-testid="collector-nudge-preview"
            >
              Preview profile
            </Link>
            <button
              onClick={dismiss}
              className="text-[11px] text-zinc-500 hover:text-zinc-800 ml-1"
              data-testid="collector-nudge-later"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
